
import React from 'react';
import { Slider } from '@/components/ui/slider';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Region, ChurchPresence } from '../data/types/region-types';
import { useLanguage } from '../contexts/language';
import SpecialRegionsSearch from './filters/SpecialRegionsSearch';
import { Switch } from './ui/switch';
import { Toggle } from './ui/toggle';
import { CircleDot, Eye, EyeOff, MapPin } from 'lucide-react';

export interface FilterControlsProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  churchPresenceFilter: ChurchPresence | 'All';
  setChurchPresenceFilter: (filter: ChurchPresence | 'All') => void;
  fundingFilter: [number, number];
  setFundingFilter: (filter: [number, number]) => void;
  resetFilters: () => void;
  regions: Region[];
  onRegionSelect: (region: Region) => void;
  onCitySelect?: (cityId: string) => void;
  isChiapasActive?: boolean;
  isHidalgoActive?: boolean;
  
  // Pin visibility toggles
  showFunding?: boolean;
  setShowFunding?: (show: boolean) => void;
  showLowPresence?: boolean;
  setShowLowPresence?: (show: boolean) => void;
  showUnreached?: boolean;
  setShowUnreached?: (show: boolean) => void;
  showAreasOfNeed?: boolean;
  setShowAreasOfNeed?: (show: boolean) => void;
}

const FilterControls: React.FC<FilterControlsProps> = ({
  searchTerm,
  setSearchTerm,
  churchPresenceFilter,
  setChurchPresenceFilter,
  fundingFilter,
  setFundingFilter,
  resetFilters,
  regions,
  onRegionSelect,
  onCitySelect,
  isChiapasActive,
  isHidalgoActive,
  
  // Pin toggles with defaults
  showFunding = true,
  setShowFunding = () => {},
  showLowPresence = true,
  setShowLowPresence = () => {},
  showUnreached = true,
  setShowUnreached = () => {},
  showAreasOfNeed = true,
  setShowAreasOfNeed = () => {}
}) => {
  const { t } = useLanguage();
  
  const handleResetFilters = () => {
    resetFilters();
  };
  
  return (
    <div className="space-y-4">
      {/* Special Regions Search */}
      <div className="mb-4">
        <SpecialRegionsSearch 
          onCitySelect={onCitySelect || (() => {})} 
          activeRegion={isChiapasActive ? 'CHIS' : isHidalgoActive ? 'HGO' : null}
        />
      </div>
      
      <Separator />
      
      {/* Pin Visibility Toggles */}
      <div>
        <Label className="text-sm font-medium mb-3 block">{t('pinVisibility') || 'Pin Visibility'}</Label>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CircleDot className="h-4 w-4 text-blue-500" />
              <span className="text-sm">{t('fundingLabels') || 'Funding Labels'}</span>
            </div>
            <Switch 
              checked={showFunding} 
              onCheckedChange={setShowFunding} 
              aria-label="Toggle funding labels"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-orange-500" />
              <span className="text-sm">{t('lowPresence') || 'Low Presence'}</span>
            </div>
            <Switch 
              checked={showLowPresence} 
              onCheckedChange={setShowLowPresence} 
              aria-label="Toggle low presence pins"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-red-500" />
              <span className="text-sm">{t('unreached') || 'Unreached'}</span>
            </div>
            <Switch 
              checked={showUnreached} 
              onCheckedChange={setShowUnreached} 
              aria-label="Toggle unreached pins"
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-purple-500" />
              <span className="text-sm">{t('areasOfNeed') || 'Areas of Need'}</span>
            </div>
            <Switch 
              checked={showAreasOfNeed} 
              onCheckedChange={setShowAreasOfNeed} 
              aria-label="Toggle areas of need pins"
            />
          </div>
        </div>
      </div>
      
      <Separator />
      
      {/* Church presence filter */}
      <div>
        <Label className="text-sm font-medium mb-1.5 block">{t('churchPresence')}</Label>
        <RadioGroup 
          defaultValue="All" 
          value={churchPresenceFilter}
          onValueChange={(value) => setChurchPresenceFilter(value as ChurchPresence | 'All')}
          className="flex flex-col space-y-1.5"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="All" id="all" />
            <Label htmlFor="all">{t('all')}</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Good" id="good" />
            <Label htmlFor="good">{t('good')}</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Medium" id="medium" />
            <Label htmlFor="medium">{t('medium')}</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Low" id="low" />
            <Label htmlFor="low">{t('low')}</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="Unreached" id="unreached" />
            <Label htmlFor="unreached">{t('unreached')}</Label>
          </div>
        </RadioGroup>
      </div>
      
      <Separator />
      
      {/* Funding range slider */}
      <div>
        <div className="flex justify-between items-center mb-1.5">
          <Label className="text-sm font-medium">{t('fundingPercentage')}</Label>
          <span className="text-sm text-muted-foreground">{fundingFilter[0]}% - {fundingFilter[1]}%</span>
        </div>
        <Slider
          defaultValue={[0, 100]}
          value={fundingFilter}
          onValueChange={(values) => setFundingFilter(values as [number, number])}
          min={0}
          max={100}
          step={5}
          className="mb-6"
        />
      </div>
      
      {/* Reset button */}
      <Button 
        variant="outline" 
        size="sm" 
        onClick={handleResetFilters}
        className="w-full"
      >
        {t('resetFilters')}
      </Button>
    </div>
  );
};

export default FilterControls;
